import clsx from 'clsx';
import React from 'react';
import { TbArrowsExchange2 } from 'react-icons/tb';

import { imageVariantLink } from '@/common/media';
import { ActionsButton, type ActionMenuItem } from '@/components/ActionsButton/ActionsButton';
import { AuthorizedImage } from '@/components/AuthorizedImage';
import { DeclinedBadge } from '@/components/DeclinedBadge/DeclinedBadge';
import type { EventResponse, SubActivity } from '@/store/events/events.dto';
import { formatIsoToTime12h } from '@/utils/timeFormatter.ts';

import styles from './UpcomingEventWidget.module.scss';

interface EventCardProps {
  event: EventResponse | SubActivity;
  onClick?: () => void;
  actions?: ActionMenuItem[];
  status?: string;
  declineReason?: string;
  compact?: boolean;
}

const EVENTS_TYPES_COLORS_MAP: Record<string, string> = {
  general: '#38BDF8',
  out_of_water: '#F59E0B',
};

export const EventItem: React.FC<EventCardProps> = ({
  event,
  onClick,
  actions,
  status,
  declineReason,
  compact,
}) => {
  const image = 'image' in event ? imageVariantLink(event.image, 450, 109) : null;
  const type = 'type' in event ? event.type : undefined;

  const declinedBadge = status === 'declined' && <DeclinedBadge reason={declineReason} />;

  if (image) {
    return (
      <div
        className={clsx(
          styles.eventCard,
          styles.eventCardWithImage,
          compact && styles.eventCardCompact,
        )}
        onClick={onClick}
      >
        <AuthorizedImage src={image} alt={event.title} className={styles.eventImage} />
        <div className={styles.eventCardBody}>
          <div className={styles.eventTimesStack}>
            <span className={styles.eventTime}>
              {formatIsoToTime12h(event.time_slot?.start ?? '')}
            </span>
            <span className={styles.eventTime}>
              {formatIsoToTime12h(event.time_slot?.end ?? '')}
            </span>
          </div>
          <span className={styles.eventTitle}>{event.title}</span>
          {declinedBadge}
          {actions && <ActionsButton items={actions} />}
        </div>
      </div>
    );
  }

  return (
    <div className={clsx(styles.eventCard, compact && styles.eventCardCompact)} onClick={onClick}>
      <span
        className={styles.dot}
        style={{ backgroundColor: type ? EVENTS_TYPES_COLORS_MAP[type] : undefined }}
      />
      <span className={styles.eventTime}>
        {formatIsoToTime12h(event.time_slot?.start ?? '')}
        <TbArrowsExchange2 className={styles.timeArrow} />
        {formatIsoToTime12h(event.time_slot?.end ?? '')}
      </span>
      <span className={styles.eventTitle}>{event.title}</span>
      {declinedBadge}
      {actions && <ActionsButton items={actions} />}
    </div>
  );
};
